import { useCallback, useState } from 'react';

import { dayOf, describe } from '../../dates';
import { useLanguage } from '../../contexts/language';
import { useInvitationChanges } from '../../hooks/invitation-changes';

import styles from './style.module.css';

// One invitation the clinician sent, on Invite patient: who it went to, where
// it stands, and what can be done with it. A pending or expired invitation
// can be sent again (which gives it another 30 days) or withdrawn; a
// declined one is the patient's answer and only says so. onChange reads the
// list again after a change; a refusal, such as resending too soon, shows
// under the row.
export function SentInvitation({ invitation, onChange }) {
  const { text, language, translate } = useLanguage();
  const { resend, withdraw } = useInvitationChanges();
  const [ busy, setBusy ] = useState(false);
  const [ error, setError ] = useState();
  const [ resent, setResent ] = useState(false);
  const { id, email, name, status, sentAt, respondedAt } = invitation;

  const act = useCallback(function(change) {
    setBusy(true);
    setError();
    setResent(false);

    // Settles with whether the change went through, so Resend can say so
    // only when it did.
    return change(id).then(function() {
      return onChange();
    }).then(function() {
      setBusy(false);

      return true;
    }).catch(function(failed) {
      setBusy(false);
      setError(failed.message);

      return false;
    });
  }, [ id, onChange ]);

  const onResend = useCallback(function() {
    return act(resend).then(function(done) {
      if (done === true) {
        setResent(true);
      }
    });
  }, [ act, resend ]);

  const onWithdraw = useCallback(function() {
    return act(withdraw);
  }, [ act, withdraw ]);

  let who = email;
  let address = null;

  if (name != null) {
    who = name;
    address = <span className={ styles.detail }>{ email }</span>;
  }

  const sent = describe({ day: dayOf(sentAt), language, short: true });
  let detail = text.invitedNotAnswered.replace('{date}', sent);
  let actions = (
    <div className={ styles.actions }>
      <button className={ styles.action } type='button' disabled={ busy } onClick={ onResend }>{ text.resendInvitation }</button>
      <button className={ styles.action } type='button' disabled={ busy } onClick={ onWithdraw }>{ text.withdraw }</button>
    </div>
  );

  if (status === 'EXPIRED') {
    detail = text.invitedExpired.replace('{date}', sent);
  }

  if (status === 'DECLINED') {
    detail = text.declinedOn.replace('{date}', describe({ day: dayOf(respondedAt), language, short: true }));
    actions = null;
  }

  let message = null;

  if (error != null) {
    message = <span className={ styles.error } role='alert'>{ translate(error) }</span>;
  }

  if (resent === true) {
    message = <span className={ styles.done } role='status'>{ text.sentAgain }</span>;
  }

  return (
    <div className={ styles.row }>
      <div className={ styles.main }>
        <span className={ styles.lines }>
          <span className={ styles.who }>{ who }</span>

          { address }

          <span className={ styles.detail }>{ detail }</span>
        </span>

        { actions }
      </div>

      { message }
    </div>
  );
}
